import React, { useState } from 'react';
import { ShieldAlert, X, Loader2, Check, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext.jsx';
import { toast } from 'sonner';

/**
 * App-wide banner shown when authenticated user has email_verified === false.
 * Lets them enter the 6-digit code or resend it without leaving the page.
 */
const VerifyEmailBanner = () => {
  const { isAuthenticated, user, verifyEmail, resendVerification } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    return sessionStorage.getItem('rcm_verify_dismissed') === 'true';
  });

  // Don't render when: not logged in, already verified, or dismissed for this session
  if (!isAuthenticated || !user || user.email_verified || dismissed) return null;

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      toast.error('Please enter the 6-digit code from your email');
      return;
    }
    setSubmitting(true);
    try {
      await verifyEmail(code);
      toast.success('Email verified — thank you!');
      setExpanded(false);
      setCode('');
    } catch (err) {
      toast.error(err.message);
    }
    setSubmitting(false);
  };

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    try {
      await resendVerification();
      toast.success('New code sent! Check your inbox AND spam folder.');
    } catch (err) {
      toast.error(err.message);
    }
    setResending(false);
  };

  const handleDismiss = () => {
    sessionStorage.setItem('rcm_verify_dismissed', 'true');
    setDismissed(true);
  };

  return (
    <div
      data-testid="verify-email-banner"
      style={{
        background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
        color: 'white',
        padding: expanded ? '14px 16px' : '12px 16px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        borderBottom: '1px solid rgba(0,0,0,0.1)'
      }}
    >
      <div className="container mx-auto" style={{ display: 'flex', alignItems: expanded ? 'flex-start' : 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '260px' }}>
          <ShieldAlert size={20} style={{ flexShrink: 0 }} />
          <div style={{ fontSize: '13px', lineHeight: 1.4 }}>
            <strong>Verify your email to place orders.</strong> We sent a 6-digit code to <strong>{user.email}</strong>.
            {!expanded && <span style={{ opacity: 0.9 }}> Didn&apos;t get it? Check spam, then tap <strong>Resend</strong>.</span>}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {!expanded ? (
            <>
              <button
                onClick={() => setExpanded(true)}
                data-testid="verify-banner-enter-code"
                style={{ background: 'white', color: '#b45309', border: 'none', padding: '8px 14px', borderRadius: '4px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
              >
                Enter Code
              </button>
              <button
                onClick={handleResend}
                disabled={resending}
                data-testid="verify-banner-resend"
                style={{
                  background: '#fbbf24',
                  color: '#78350f',
                  border: 'none',
                  padding: '8px 14px',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: resending ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: resending ? 0.7 : 1,
                }}
              >
                {resending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                {resending ? 'Sending…' : 'Resend Email'}
              </button>
            </>
          ) : (
            <form onSubmit={handleVerify} style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                data-testid="verify-banner-code-input"
                autoFocus
                style={{
                  padding: '6px 10px',
                  borderRadius: '4px',
                  border: '1px solid rgba(255,255,255,0.6)',
                  background: 'rgba(255,255,255,0.1)',
                  color: 'white',
                  fontSize: '14px',
                  letterSpacing: '4px',
                  fontFamily: 'monospace',
                  width: '110px',
                  textAlign: 'center'
                }}
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="verify-banner-submit"
                style={{ background: '#10b981', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                {submitting ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Verify
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                data-testid="verify-banner-resend-inline"
                style={{
                  background: '#fbbf24',
                  color: '#78350f',
                  border: 'none',
                  padding: '6px 10px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: resending ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  opacity: resending ? 0.7 : 1,
                }}
              >
                {resending ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />}
                {resending ? 'Sending' : 'Resend'}
              </button>
              <button
                type="button"
                onClick={() => { setExpanded(false); setCode(''); }}
                style={{ background: 'transparent', color: 'white', border: 'none', padding: '6px', cursor: 'pointer', display: 'flex' }}
                title="Cancel"
              >
                <X size={14} />
              </button>
            </form>
          )}

          <button
            onClick={handleDismiss}
            title="Dismiss for this session"
            data-testid="verify-banner-dismiss"
            style={{ background: 'transparent', color: 'white', border: 'none', padding: '4px', cursor: 'pointer', display: 'flex', opacity: 0.7 }}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailBanner;
